import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/**
 * Climate targets module (T-01/T-02): author targets with the attributes
 * AASB S2 requires; edits are restatements (new versions with a reason).
 * Current targets render in the S2 report's section 4.5.
 */
interface Target {
	targetID: number;
	version: number;
	name: string;
	objective: string;
	metric: string;
	targetType: "absolute" | "intensity";
	methodology: string | null;
	sectoralApproach: number;
	baseYear: number;
	baseTonnes: number;
	targetYear: number;
	reductionPct: number;
	restatementReason: string | null;
}

const emptyForm = () => ({
	name: "",
	objective: "",
	metric: "Gross t CO2e (Scopes 1+2+3)",
	targetType: "absolute" as Target["targetType"],
	methodology: "SBTi absolute contraction (1.5°C)",
	sectoralApproach: false,
	baseYear: String(new Date().getFullYear() - 1),
	baseTonnes: "",
	targetYear: "2030",
	reductionPct: "",
	restatementReason: "",
});

const ClimateTargets: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [targets, setTargets] = useState<Target[]>([]);
	const [available, setAvailable] = useState(true);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [dialog, setDialog] = useState<null | { mode: "create" } | { mode: "restate"; target: Target }>(null);
	const [form, setForm] = useState(emptyForm());
	const [saving, setSaving] = useState(false);
	const [history, setHistory] = useState<null | { name: string; loading: boolean; versions: any[] }>(null);

	const authFetch = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			return fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await authFetch("/api/targets");
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not load targets");
			setAvailable((json.Data || json).available !== false);
			setTargets((json.Data || json).targets || []);
		} catch (e: any) {
			setError(e.message || "Could not load targets");
		} finally {
			setLoading(false);
		}
	}, [authFetch]);

	useEffect(() => {
		load();
	}, [load]);

	const openCreate = () => {
		setForm(emptyForm());
		setDialog({ mode: "create" });
	};

	const openRestate = (t: Target) => {
		setForm({
			name: t.name,
			objective: t.objective,
			metric: t.metric,
			targetType: t.targetType,
			methodology: t.methodology || "",
			sectoralApproach: !!t.sectoralApproach,
			baseYear: String(t.baseYear),
			baseTonnes: String(t.baseTonnes),
			targetYear: String(t.targetYear),
			reductionPct: String(t.reductionPct),
			restatementReason: "",
		});
		setDialog({ mode: "restate", target: t });
	};

	const save = async () => {
		if (!dialog) return;
		setSaving(true);
		setError(null);
		try {
			const body = {
				name: form.name,
				objective: form.objective,
				metric: form.metric,
				targetType: form.targetType,
				methodology: form.methodology || undefined,
				sectoralApproach: form.sectoralApproach,
				baseYear: form.baseYear,
				baseTonnes: form.baseTonnes,
				targetYear: form.targetYear,
				reductionPct: form.reductionPct,
				...(dialog.mode === "restate"
					? { restatementReason: form.restatementReason }
					: {}),
			};
			const res = await authFetch(
				dialog.mode === "create"
					? "/api/targets"
					: `/api/targets/${dialog.target.targetID}`,
				{
					method: dialog.mode === "create" ? "POST" : "PUT",
					body: JSON.stringify(body),
				},
			);
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not save the target");
			setDialog(null);
			await load();
		} catch (e: any) {
			setError(e.message || "Could not save the target");
		} finally {
			setSaving(false);
		}
	};

	const retire = async (t: Target) => {
		try {
			await authFetch(`/api/targets/${t.targetID}`, { method: "DELETE" });
			await load();
		} catch {
			setError("Could not retire the target");
		}
	};

	const openHistory = async (t: Target) => {
		setHistory({ name: t.name, loading: true, versions: [] });
		try {
			const res = await authFetch(`/api/targets/${t.targetID}/history`);
			const json = await res.json().catch(() => ({}));
			setHistory({
				name: t.name,
				loading: false,
				versions: (json.Data || json).versions || [],
			});
		} catch {
			setHistory(null);
			setError("Could not load target history");
		}
	};

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={2} sx={{ p: 4 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
					<Box sx={{ flex: 1, minWidth: 260 }}>
						<Typography variant="h5">Climate targets</Typography>
						<Typography variant="body2" color="text.secondary">
							Each target carries the attributes AASB S2 requires
							(objective, metric, absolute or intensity,
							methodology, base and target period). Changing a
							target is a restatement — every version is kept and
							disclosed. Current targets render in the S2 report.
						</Typography>
					</Box>
					<Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!available}>
						Add target
					</Button>
				</Box>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
				)}
				{!available && !loading && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Target storage is not provisioned on this environment yet (migration 023).
					</Alert>
				)}

				{loading ? (
					<Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>
				) : targets.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
						No targets yet. Until one is added, the S2 report shows
						the platform's default SBTi trajectories.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Target</TableCell>
								<TableCell>Type</TableCell>
								<TableCell align="right">Base year</TableCell>
								<TableCell align="right">Base t CO₂e</TableCell>
								<TableCell align="right">Target year</TableCell>
								<TableCell align="right">Reduction</TableCell>
								<TableCell>Version</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{targets.map((t) => (
								<TableRow key={t.targetID}>
									<TableCell>
										<strong>{t.name}</strong>
										<Typography variant="caption" display="block" color="text.secondary">
											{t.objective}
										</Typography>
									</TableCell>
									<TableCell>
										{t.targetType === "intensity" ? "Intensity" : "Absolute"}
									</TableCell>
									<TableCell align="right">{t.baseYear}</TableCell>
									<TableCell align="right">{Number(t.baseTonnes).toLocaleString()}</TableCell>
									<TableCell align="right">{t.targetYear}</TableCell>
									<TableCell align="right">−{Number(t.reductionPct)}%</TableCell>
									<TableCell>
										<Chip
											size="small"
											label={`v${t.version}`}
											color={t.version > 1 ? "warning" : "default"}
										/>
									</TableCell>
									<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
										<IconButton size="small" onClick={() => openRestate(t)} aria-label="restate">
											<EditOutlinedIcon fontSize="small" />
										</IconButton>
										<IconButton size="small" onClick={() => openHistory(t)} aria-label="history">
											<HistoryOutlinedIcon fontSize="small" />
										</IconButton>
										<IconButton size="small" onClick={() => retire(t)} aria-label="retire">
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>

			<Dialog open={dialog !== null} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
				<DialogTitle>
					{dialog?.mode === "restate" ? `Restate: ${dialog.target.name}` : "Add climate target"}
				</DialogTitle>
				<DialogContent dividers sx={{ display: "grid", gap: 2, pt: 2 }}>
					{dialog?.mode === "restate" && (
						<TextField
							size="small"
							label="Reason for the restatement"
							value={form.restatementReason}
							onChange={(e) => setForm({ ...form, restatementReason: e.target.value })}
							required
							helperText="Required — restatements are disclosed with the target."
						/>
					)}
					<TextField size="small" label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required sx={{ mt: 1 }} />
					<TextField
						size="small"
						label="Objective"
						value={form.objective}
						onChange={(e) => setForm({ ...form, objective: e.target.value })}
						multiline
						minRows={2}
						required
						helperText="What the target commits to, in one or two sentences."
					/>
					<Box sx={{ display: "flex", gap: 2 }}>
						<TextField
							size="small"
							select
							label="Type"
							value={form.targetType}
							onChange={(e) => setForm({ ...form, targetType: e.target.value as Target["targetType"] })}
							sx={{ flex: 1 }}
						>
							<MenuItem value="absolute">Absolute</MenuItem>
							<MenuItem value="intensity">Intensity</MenuItem>
						</TextField>
						<TextField size="small" label="Metric" value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} sx={{ flex: 2 }} />
					</Box>
					<TextField
						size="small"
						label="Methodology (optional)"
						value={form.methodology}
						onChange={(e) => setForm({ ...form, methodology: e.target.value })}
						helperText="e.g. SBTi absolute contraction (1.5°C)"
					/>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={form.sectoralApproach}
								onChange={(e) => setForm({ ...form, sectoralApproach: e.target.checked })}
							/>
						}
						label="Informed by a sectoral decarbonisation approach"
					/>
					<Box sx={{ display: "flex", gap: 2 }}>
						<TextField size="small" label="Base year" type="number" value={form.baseYear} onChange={(e) => setForm({ ...form, baseYear: e.target.value })} sx={{ flex: 1 }} />
						<TextField size="small" label="Base year t CO₂e" type="number" value={form.baseTonnes} onChange={(e) => setForm({ ...form, baseTonnes: e.target.value })} sx={{ flex: 1 }} />
					</Box>
					<Box sx={{ display: "flex", gap: 2 }}>
						<TextField size="small" label="Target year" type="number" value={form.targetYear} onChange={(e) => setForm({ ...form, targetYear: e.target.value })} sx={{ flex: 1 }} />
						<TextField
							size="small"
							label="Reduction at target year (%)"
							type="number"
							value={form.reductionPct}
							onChange={(e) => setForm({ ...form, reductionPct: e.target.value })}
							sx={{ flex: 1 }}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialog(null)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={save}
						disabled={saving || (dialog?.mode === "restate" && !form.restatementReason.trim())}
					>
						{saving ? "Saving…" : dialog?.mode === "restate" ? "Restate target" : "Save target"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={history !== null} onClose={() => setHistory(null)} fullWidth maxWidth="sm">
				<DialogTitle>{history?.name} — versions</DialogTitle>
				<DialogContent dividers>
					{history?.loading ? (
						<Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>
					) : (
						history?.versions.map((v) => (
							<Paper key={v.targetID} variant="outlined" sx={{ p: 2, mb: 2 }}>
								<Typography variant="subtitle2">
									v{v.version}
									{v.isCurrent ? " · current" : ""}
									{v.createdBy ? ` · ${v.createdBy}` : ""}
								</Typography>
								<Typography variant="body2">
									{v.baseYear} base ({Number(v.baseTonnes).toLocaleString()} t) → −{Number(v.reductionPct)}% by {v.targetYear}
								</Typography>
								{v.restatementReason && (
									<Typography variant="caption" color="text.secondary">
										Restated: {v.restatementReason}
									</Typography>
								)}
							</Paper>
						))
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setHistory(null)}>Close</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};

export default ClimateTargets;
